import { api, getAuthToken, getClientId, API_BASE_URL } from '../../../api/client';
import { io, Socket } from 'socket.io-client';
import type {
  Element,
  ElementInput,
  Schedule,
  ScheduleEntry,
  ScheduleEntryInput,
  DayOfWeek,
} from '@thekwallace/backtime-shared';

// Re-export getClientId for convenience (defined in api/client.ts)
export { getClientId };

// WebSocket connection
let socket: Socket | null = null;
let currentTimelineId: string | null = null; // Track current timeline for reconnection
let wsConnectionListeners: Set<(connected: boolean) => void> = new Set(); // Listeners for connection status changes
let lastSequences: Map<string, number> = new Map(); // Track last seen sequence per timeline for gap detection

// =============================================================================
// SCHEDULE CONVERSION HELPERS
// =============================================================================

/**
 * Convert Schedule (UI format) to ScheduleEntryInput[] (API format)
 */
export function scheduleToEntries(schedule: Schedule): ScheduleEntryInput[] {
  const entries: ScheduleEntryInput[] = [];
  const days: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  for (const day of days) {
    const times = schedule[day] || [];
    for (const time of times) {
      entries.push({ dayOfWeek: day, time });
    }
  }

  return entries;
}

/**
 * Convert ScheduleEntry[] (API format) to Schedule (UI format)
 */
export function entriesToSchedule(entries: ScheduleEntry[]): Schedule {
  const schedule: Schedule = {};

  for (const entry of entries) {
    const day = entry.dayOfWeek;
    if (!schedule[day]) {
      schedule[day] = [];
    }
    schedule[day]!.push(entry.time);
  }

  // Sort times within each day
  for (const day of Object.keys(schedule) as DayOfWeek[]) {
    schedule[day]?.sort();
  }

  return schedule;
}

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

/**
 * Timeline data returned from API
 * Uses typed elements and scheduleEntries (not JSON phases/schedule)
 */
export interface TimelineData {
  id: string;
  name: string;
  description?: string | null;
  color?: string | null;
  ownerId: string;
  isPublic: boolean;
  publicCode: string | null;
  elements: Element[];
  scheduleEntries: ScheduleEntry[];
  selectedStartTime?: string | null;
  timeOffset: number;
  timezone?: string;
  activeStartTime?: string | null;
  autoAdvanceBuffer: number;
  isAutoAdvanceEnabled: boolean;
  urgencySettings?: {
    imminent?: { thresholdMinutes: number; color?: string };
    soon?: { thresholdMinutes: number; color?: string };
  } | null;
  isSubscribed?: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Timeline list item (summary)
 */
export interface TimelineListItem {
  id: string;
  name: string;
  ownerId: string;
  isPublic: boolean;
  publicCode?: string | null;
  updatedAt: string;
}

/**
 * Create timeline input
 */
export interface CreateTimelineInput {
  name?: string;
  isPublic?: boolean;
  elements?: ElementInput[];
  scheduleEntries?: ScheduleEntryInput[];
  selectedStartTime?: string | null;
  timeOffset?: number;
  timezone?: string;
  autoActivate?: boolean;
}

/**
 * Update timeline input
 */
export interface UpdateTimelineInput {
  name?: string;
  description?: string | null;
  color?: string | null;
  isPublic?: boolean;
  elements?: ElementInput[];
  scheduleEntries?: ScheduleEntryInput[];
  selectedStartTime?: string | null;
  timeOffset?: number;
  timezone?: string;
  isAutoAdvanceEnabled?: boolean;
  autoAdvanceBuffer?: number;
}

/**
 * WebSocket update data
 */
interface TimelineUpdateData {
  timelineId: string;
  sequence?: number;
  timestamp?: string;
  sourceClientId?: string;
  timeline?: TimelineData;
  data?: Partial<TimelineData>;
}

/**
 * Resync required data
 */
interface ResyncData {
  timelineId: string;
  currentSequence?: number;
}

/**
 * Notify all connection listeners of status change
 */
function notifyConnectionListeners(connected: boolean): void {
  wsConnectionListeners.forEach(listener => {
    try {
      listener(connected);
    } catch (error) {
      console.error('Error in connection status listener:', error);
    }
  });
}

/**
 * Timeline service for managing shared service timer timelines
 * Handles timeline CRUD, public sharing, and real-time updates
 */
const timelineService = {
  /**
   * Initialize WebSocket connection
   */
  initSocket(): Socket {
    if (!socket) {
      const token = getAuthToken();
      socket = io(API_BASE_URL, {
        autoConnect: false,
        auth: {
          token: token
        },
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000
      });

      // Rejoin timeline room on reconnection
      socket.on('connect', () => {
        console.log('WebSocket connected');
        notifyConnectionListeners(true);
        if (currentTimelineId) {
          console.log('Rejoining timeline:', currentTimelineId);
          socket!.emit('join-timeline', currentTimelineId);
        }
      });

      socket.on('disconnect', (reason) => {
        console.log('WebSocket disconnected:', reason);
        notifyConnectionListeners(false);
      });

      socket.on('reconnect', (attemptNumber) => {
        console.log('WebSocket reconnected after', attemptNumber, 'attempts');
      });

      socket.on('reconnect_error', (error) => {
        console.log('WebSocket reconnect error:', error.message);
      });
    }
    return socket;
  },

  /**
   * Get socket instance
   */
  getSocket(): Socket {
    if (!socket) {
      this.initSocket();
    }
    return socket!;
  },

  /**
   * Generate a short public code for timeline sharing (6 characters)
   */
  generatePublicCode(): string {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  },

  /**
   * Create a new timeline
   */
  async createTimeline(_ownerId: string, timelineData: CreateTimelineInput = {}): Promise<string> {
    try {
      // Get user's timezone from browser
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Default schedule entries (Sunday times)
      const defaultScheduleEntries: ScheduleEntryInput[] = [
        { dayOfWeek: 'sunday', time: '08:30:00' },
        { dayOfWeek: 'sunday', time: '10:00:00' },
        { dayOfWeek: 'sunday', time: '11:30:00' },
        { dayOfWeek: 'sunday', time: '13:00:00' },
      ];

      const response = await api.post<{ timeline: TimelineData }>('/timelines', {
        name: timelineData.name || 'My Timeline',
        isPublic: false,
        elements: timelineData.elements || [],
        scheduleEntries: timelineData.scheduleEntries || defaultScheduleEntries,
        selectedStartTime: timelineData.selectedStartTime || null,
        timeOffset: timelineData.timeOffset || 0,
        timezone: timelineData.timezone || userTimezone || 'America/Chicago'
      });

      return response.timeline.id;
    } catch (error) {
      console.error('Error creating timeline:', error);
      throw error;
    }
  },

  /**
   * Get timeline by ID
   */
  async getTimeline(timelineId: string): Promise<TimelineData> {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }

    try {
      const response = await api.get<{ timeline: TimelineData }>(`/timelines/${timelineId}`);
      return response.timeline;
    } catch (error) {
      console.error('Error getting timeline:', error);
      throw error;
    }
  },

  /**
   * Get timeline by public code
   */
  async getTimelineByPublicCode(publicCode: string): Promise<TimelineData | null> {
    if (!publicCode) {
      throw new Error('Public code is required');
    }

    try {
      const response = await api.get<{ timeline: TimelineData }>(`/timelines/public/${publicCode}`);
      return response.timeline;
    } catch (error) {
      console.error('Error getting timeline by public code:', error);
      return null;
    }
  },

  /**
   * Update timeline data
   * @param timelineId - The timeline ID to update
   * @param updates - The fields to update
   * @param options - Optional settings including ifUnmodifiedSince for conflict detection
   */
  async updateTimeline(
    timelineId: string,
    updates: UpdateTimelineInput,
    options?: { ifUnmodifiedSince?: string }
  ): Promise<TimelineData> {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }

    try {
      const response = await api.put<{ timeline: TimelineData }>(
        `/timelines/${timelineId}`,
        updates,
        options
      );
      return response.timeline;
    } catch (error) {
      console.error('Error updating timeline:', error);
      throw error;
    }
  },

  /**
   * Make timeline public or private
   */
  async setTimelinePublic(timelineId: string, isPublic: boolean): Promise<TimelineData> {
    return this.updateTimeline(timelineId, { isPublic });
  },

  /**
   * Get user's active timeline ID
   */
  async getUserActiveTimeline(userId: string): Promise<string | null> {
    if (!userId) {
      return null;
    }

    try {
      const user = await api.get<{ activeTimelineId?: string }>('/auth/me');

      // If user has an active timeline, return it
      if (user.activeTimelineId) {
        return user.activeTimelineId;
      }

      // Fallback: check if user has any owned timelines
      try {
        const { timelines } = await api.get<{ timelines: TimelineListItem[] }>(`/users/${userId}/timelines`);
        if (timelines && timelines.length > 0) {
          // Return the most recently updated timeline
          return timelines[0].id;
        }
      } catch (err) {
        console.error('Error fetching user timelines:', err);
      }

      return null;
    } catch (error) {
      console.error('Error getting user active timeline:', error);
      return null;
    }
  },

  /**
   * Subscribe to real-time timeline updates via WebSocket
   * @param timelineId - Timeline to subscribe to
   * @param callback - Called with timeline data and optional sourceClientId (for detecting own updates)
   */
  subscribeToTimeline(timelineId: string, callback: (timeline: TimelineData | Partial<TimelineData> | null, sourceClientId?: string) => void): () => void {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }

    const socket = this.getSocket();

    // Track current timeline for reconnection
    currentTimelineId = timelineId;

    // Update auth token before connecting (in case it changed)
    const token = getAuthToken();
    if (token) {
      socket.auth = { token };
    }

    // Connect if not already connected
    if (!socket.connected) {
      socket.connect();
    }

    // Join the timeline room (use new event name)
    socket.emit('join-timeline', timelineId);

    // Get initial sequence number for this timeline
    socket.emit('get-sequence', timelineId, (sequence: number) => {
      console.log(`Initial sequence for timeline ${timelineId}: ${sequence}`);
      lastSequences.set(timelineId, sequence);
    });

    // Listen for timeline updates with acknowledgement support
    const onTimelineUpdate = (data: TimelineUpdateData, ackCallback?: (ack: { received: boolean; sequence: number }) => void) => {
      if (data.timelineId === timelineId) {
        const { sequence, timestamp } = data;

        // Check for sequence gaps if we have a previous sequence
        if (sequence !== undefined) {
          const lastSeq = lastSequences.get(timelineId) || 0;

          if (sequence > lastSeq + 1 && lastSeq > 0) {
            // Gap detected - we missed some messages
            console.warn(`Sequence gap detected for timeline ${timelineId}: expected ${lastSeq + 1}, got ${sequence}`);
            // Request resync
            socket.emit('request-resync', { timelineId, lastSequence: lastSeq });
          }

          // Update last seen sequence
          lastSequences.set(timelineId, sequence);

          console.log(`Received update for timeline ${timelineId}, sequence ${sequence}${timestamp ? `, timestamp ${new Date(timestamp).toISOString()}` : ''}`);
        }

        // Send acknowledgement if callback provided
        if (ackCallback && typeof ackCallback === 'function') {
          ackCallback({ received: true, sequence: sequence || 0 });
          console.log(`Sent ack for timeline ${timelineId}, sequence ${sequence}`);
        }

        // Handle both full timeline updates and partial updates
        // Pass sourceClientId so caller can detect their own updates
        if (data.timeline) {
          callback(data.timeline, data.sourceClientId);
        } else if (data.data) {
          // Partial update (like activeStartTime change)
          callback(data.data, data.sourceClientId);
        }
      }
    };

    // Handle resync required event
    const onResyncRequired = (data: ResyncData) => {
      if (data.timelineId === timelineId) {
        console.log(`Resync required for timeline ${timelineId}, fetching full state...`);
        // Fetch full timeline state from REST API
        this.getTimeline(timelineId)
          .then(timeline => {
            // Update sequence to current
            if (data.currentSequence !== undefined) {
              lastSequences.set(timelineId, data.currentSequence);
            }
            callback(timeline);
            console.log(`Resync complete for timeline ${timelineId}`);
          })
          .catch(error => {
            console.error('Error during resync:', error);
          });
      }
    };

    socket.on('timeline-updated', onTimelineUpdate);
    socket.on('resync-required', onResyncRequired);

    // Fetch initial timeline data
    this.getTimeline(timelineId)
      .then(timeline => callback(timeline))
      .catch(error => {
        console.error('Error fetching initial timeline:', error);
        callback(null);
      });

    // Return unsubscribe function
    return () => {
      socket.off('timeline-updated', onTimelineUpdate);
      socket.off('resync-required', onResyncRequired);
      socket.emit('leave-timeline', timelineId);
      // Clear tracked timeline if it matches
      if (currentTimelineId === timelineId) {
        currentTimelineId = null;
      }
      // Clean up sequence tracking
      lastSequences.delete(timelineId);
    };
  },

  /**
   * Check if user can edit a timeline
   */
  async canUserEditTimeline(userId: string, timelineId: string): Promise<boolean> {
    if (!userId || !timelineId) {
      return false;
    }

    try {
      const timeline = await this.getTimeline(timelineId);
      return timeline && timeline.ownerId === userId;
    } catch (error) {
      console.error('Error checking edit permission:', error);
      return false;
    }
  },

  /**
   * Get all user timelines (owned, subscribed, or all)
   */
  async getAllUserTimelines(userId: string, type: 'owned' | 'subscribed' | 'all' = 'all'): Promise<TimelineListItem[]> {
    if (!userId) {
      throw new Error('User ID is required');
    }

    try {
      const response = await api.get<{ timelines: TimelineListItem[] }>(`/users/${userId}/timelines?type=${type}`);
      return response.timelines || [];
    } catch (error) {
      console.error('Error getting user timelines:', error);
      throw error;
    }
  },

  /**
   * Set active timeline for user
   */
  async setActiveTimeline(userId: string, timelineId: string): Promise<{ id: string }> {
    if (!userId || !timelineId) {
      throw new Error('User ID and Timeline ID are required');
    }

    try {
      const response = await api.put<{ user: { id: string } }>(`/users/${userId}/active-timeline`, {
        timelineId
      });
      return response.user;
    } catch (error) {
      console.error('Error setting active timeline:', error);
      throw error;
    }
  },

  /**
   * Duplicate a timeline
   */
  async duplicateTimeline(timelineId: string, newName: string): Promise<TimelineData> {
    if (!timelineId || !newName) {
      throw new Error('Timeline ID and new name are required');
    }

    try {
      const response = await api.post<{ timeline: TimelineData }>(`/timelines/${timelineId}/duplicate`, {
        name: newName
      });
      return response.timeline;
    } catch (error) {
      console.error('Error duplicating timeline:', error);
      throw error;
    }
  },

  /**
   * Update public code for a timeline
   */
  async updatePublicCode(timelineId: string, publicCode: string): Promise<TimelineData> {
    if (!timelineId || !publicCode) {
      throw new Error('Timeline ID and public code are required');
    }
    try {
      const response = await api.put<{ timeline: TimelineData }>(`/timelines/${timelineId}/public-code`, {
        publicCode: publicCode.toUpperCase()
      });
      return response.timeline;
    } catch (error) {
      console.error('Error updating public code:', error);
      throw error;
    }
  },

  /**
   * Delete a timeline
   */
  async deleteTimeline(timelineId: string): Promise<boolean> {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }
    try {
      await api.delete(`/timelines/${timelineId}`);
      return true;
    } catch (error) {
      console.error('Error deleting timeline:', error);
      throw error;
    }
  },

  /**
   * Subscribe to a public timeline (authenticated users)
   */
  async subscribeToTimelineById(timelineId: string): Promise<{ message: string }> {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }
    try {
      const response = await api.post<{ message: string }>(`/timelines/${timelineId}/subscribe`);
      return response;
    } catch (error) {
      console.error('Error subscribing to timeline:', error);
      throw error;
    }
  },

  /**
   * Unsubscribe from a timeline (authenticated users)
   */
  async unsubscribeFromTimeline(timelineId: string): Promise<boolean> {
    if (!timelineId) {
      throw new Error('Timeline ID is required');
    }
    try {
      await api.delete(`/timelines/${timelineId}/subscribe`);
      return true;
    } catch (error) {
      console.error('Error unsubscribing from timeline:', error);
      throw error;
    }
  },

  /**
   * Migrate local subscriptions to server (called after login)
   */
  async migrateLocalSubscriptions(localSubscriptions: Array<{ publicCode: string; name?: string }>): Promise<{ migrated: number; failed: number }> {
    if (!localSubscriptions || localSubscriptions.length === 0) {
      return { migrated: 0, failed: 0 };
    }

    let migrated = 0;
    let failed = 0;

    for (const sub of localSubscriptions) {
      try {
        // First get the timeline by public code to get its ID
        const timeline = await this.getTimelineByPublicCode(sub.publicCode);
        if (timeline && timeline.id) {
          await this.subscribeToTimelineById(timeline.id);
          migrated++;
        } else {
          failed++;
        }
      } catch (error) {
        console.error(`Failed to migrate subscription ${sub.publicCode}:`, error);
        failed++;
      }
    }

    return { migrated, failed };
  },

  /**
   * Check if WebSocket is connected
   */
  isWebSocketConnected(): boolean {
    return socket?.connected || false;
  },

  /**
   * Subscribe to WebSocket connection status changes
   */
  subscribeToConnectionStatus(callback: (connected: boolean) => void): () => void {
    wsConnectionListeners.add(callback);
    // Immediately call with current status
    callback(this.isWebSocketConnected());
    return () => wsConnectionListeners.delete(callback);
  }
};

export default timelineService;
